'use client';

import { c as _c } from "react/compiler-runtime";
import { nativeWindow } from '@todesktop/client-core';
import { useEffect } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { listenToDesktopEvent } from '@/lib/desktop-app-listener';
const goBack = () => window.history.back();
const goForward = () => window.history.forward();

/** Auxiliary mouse buttons, as `MouseEvent.button` numbers them. */
const MOUSE_BUTTON_STEPS: Partial<Record<number, () => void>> = {
  3: goBack,
  4: goForward
};

/** Swipe directions, as the desktop window reports them. */
const SWIPE_STEPS: Partial<Record<string, () => void>> = {
  left: goBack,
  right: goForward
};

/**
 * Restores browser-style back and forward controls in the desktop shell.
 * Browsers handle auxiliary mouse buttons in their own UI, but the embedded
 * ToDesktop window needs to map them to the page's history explicitly.
 *
 * On macOS, mouse drivers such as Logi Options+ send the back and forward buttons as swipe
 * gestures rather than mouse buttons, and so does a three-finger trackpad swipe. The page never
 * sees a swipe, so the window reports it. A swipe left goes back, as it does in Safari and Chrome.
 */
export function DesktopHistoryNavigationProvider() {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp2, t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  useEffect(_temp4, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      preventDefault: true,
      enableOnFormTags: false
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useHotkeys("mod+bracketleft", goBack, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      preventDefault: true,
      enableOnFormTags: false
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  useHotkeys("mod+bracketright", goForward, t3);
  return null;
}
function _temp4() {
  return listenToDesktopEvent("desktop swipe gestures", nativeWindow.on("swipe", _temp3));
}
function _temp3(direction) {
  return SWIPE_STEPS[direction]?.();
}
function _temp2() {
  const handleMouseUp = _temp;
  window.addEventListener("mouseup", handleMouseUp, true);
  return () => {
    window.removeEventListener("mouseup", handleMouseUp, true);
  };
}
function _temp(event) {
  const step = MOUSE_BUTTON_STEPS[event.button];
  if (step) {
    event.preventDefault();
    step();
  }
}